// src/services/inventoryApi.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  writeBatch,
  query,
  where,
  // limit,
  increment,
} from "firebase/firestore";
import { db } from "../services/firebase";

/* -------------------------------------------------------
 * Utils
 * ----------------------------------------------------- */

// Clé plate pour stock/{location__item}
const stockKey = (locId, itemId) => `${locId}__${itemId}`;

// Règle de pricing par défaut si aucune marge/prix explicite n'est fournie
function computeSellingPrice(cost) {
  const c = Number(cost || 0);
  if (c <= 0) return 0;
  if (c < 1000) return Math.round(c * 1.8);
  if (c < 5000) return Math.round(c * 1.6);
  if (c < 20000) return Math.round(c * 1.45);
  if (c < 50000) return Math.round(c * 1.35);
  return Math.round(c * 1.25);
}

/* =========================================================
 * 1) CREATE PO
 * - Écrit purchaseOrders/{id} avec items (array)
 * - Incrémente onOrder dans stockAgg/* via increment() (sans lecture)
 * - Conçu pour éviter "all reads before all writes"
 * ========================================================= */
export async function createPO({ supplier, lines, createdBy, amount = 0 }) {
  const poRef = doc(collection(db, "purchaseOrders"));

  const items = (lines || []).map((l) => ({
    itemId: l.itemId,
    name: l.name || null,
    qtyOrdered: Number(l.qty || 0),
    unitCost: Number(l.unitCost || 0),
    qtyReceived: 0,
  }));

  await runTransaction(db, async (tx) => {
    // PO (en-tête + lignes)
    // tx.set(poRef, {
    //   supplier: supplier || null,
    //   status: "open",
    //   amount: Number(amount || 0),
    //   items,
    //   createdAt: serverTimestamp(),
    //   updatedAt: serverTimestamp(),
    //   createdBy: createdBy || "anonymous",
    // });
    tx.set(poRef, {
      supplier: supplier || null,
      status: "open",
      amount: Number(amount || 0),
      items,
      locked: false,              // 👈 nouveau champ
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: createdBy || "anonymous",
    });

    // Incrémente onOrder — sans lecture
    for (const it of items) {
      if (!it.itemId || !it.qtyOrdered) continue;
      const aggRef = doc(db, "stockAgg", it.itemId);
      tx.set(
        aggRef,
        {
          onOrder: increment(Math.max(0, Number(it.qtyOrdered || 0))),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  });

  return { id: poRef.id };
}

/* =========================================================
 * 2) RECEIVE FROM PO
 * - Lit le PO et toutes les cibles (stockAgg + stock + itemsEK) AVANT toute écriture
 * - Met à jour items[].qtyReceived et le statut du PO (open/partial/closed)
 * - --onOrder, ++perLocation, ++total, ++stock
 * - Met à jour itemsEK.cost et itemsEK.price (auto pricing possible)
 * - Crée un receipt
 * ========================================================= */
export async function receiveFromPO({ poId, locationId, items, receivedBy }) {
  if (!poId) throw new Error("poId requis");
  if (!items?.length) throw new Error("Aucune ligne à recevoir");

  const poRef = doc(db, "purchaseOrders", poId);
  const receiptRef = doc(collection(db, "receipts"));

  // Agrège les quantités par itemId (et garde unitCost/newPrice/margin)
  const recvMap = new Map(); // itemId -> { qty, unitCost?, newPrice?, margin? }
  for (const r of items) {
    const id = r?.itemId;
    const q = Math.max(0, Number(r?.qty || 0));
    if (!id || !q) continue;
    const prev = recvMap.get(id) || { qty: 0 };
    recvMap.set(id, {
      qty: prev.qty + q,
      unitCost: r.unitCost ?? prev.unitCost,
      newPrice: r.newPrice ?? prev.newPrice,
      margin: r.margin ?? prev.margin,
    });
  }
  if (!recvMap.size) throw new Error("Aucune quantité valide à recevoir");

  const itemIds = Array.from(recvMap.keys());
  const aggRefs = itemIds.map((id) => doc(db, "stockAgg", id));
  const stockRefs = itemIds.map((id) => doc(db, "stock", stockKey(locationId, id)));
  const itemRefs = itemIds.map((id) => doc(db, "itemsEK", id));

  await runTransaction(db, async (tx) => {
    // ======= LECTURES =======
    const poSnap = await tx.get(poRef);
    if (!poSnap.exists()) throw new Error("PO introuvable");
    const po = poSnap.data();
    if (po.status === "closed") throw new Error("PO déjà clos");
    const poItems = Array.isArray(po.items) ? po.items : [];

    // index PO par itemId
    const poByItem = new Map();
    for (const l of poItems) {
      if (l?.itemId) poByItem.set(l.itemId, l);
    }

    const aggDocs = await Promise.all(aggRefs.map((r) => tx.get(r)));
    const stockDocs = await Promise.all(stockRefs.map((r) => tx.get(r)));
    const itemDocs = await Promise.all(itemRefs.map((r) => tx.get(r)));

    // Validations vs restant PO
    for (const itemId of itemIds) {
      const rec = recvMap.get(itemId);
      const line = poByItem.get(itemId);
      if (!line) throw new Error(`Ligne PO introuvable pour item ${itemId}`);
      const remain = Number(line.qtyOrdered || 0) - Number(line.qtyReceived || 0);
      if (rec.qty > remain) {
        throw new Error(`Réception > restant pour ${line.name || itemId} (reste ${remain})`);
      }
    }

    // ======= CALCULS =======
    const updatedItems = poItems.map((l) => {
      if (!l?.itemId) return l;
      const rec = recvMap.get(l.itemId);
      if (!rec) return l;
      return { ...l, qtyReceived: Number(l.qtyReceived || 0) + rec.qty };
    });

    let anyReceived = false;
    let allReceived = true;
    for (const L of updatedItems) {
      const qOrd = Number(L.qtyOrdered || 0);
      const qRec = Number(L.qtyReceived || 0);
      const remain = qOrd - qRec;
      if (qRec > 0) anyReceived = true;
      if (remain > 0) allReceived = false;
    }
    const newStatus = allReceived ? "closed" : anyReceived ? "partial" : "open";

    const newAgg = new Map();      // itemId -> { onOrder, total, perLocation }
    const newStock = new Map();    // itemId -> newQty
    const newItemVals = new Map(); // itemId -> { cost, price }

    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const rec = recvMap.get(itemId);
      const q = rec.qty;

      // stockAgg
      const aggSnap = aggDocs[i];
      const curAgg = aggSnap.exists() ? aggSnap.data() : { onOrder: 0, total: 0, perLocation: {} };
      const perLoc = { ...(curAgg.perLocation || {}) };
      perLoc[locationId] = Number(perLoc[locationId] || 0) + q;
      newAgg.set(itemId, {
        onOrder: Math.max(0, Number(curAgg.onOrder || 0) - q),
        total: Number(curAgg.total || 0) + q,
        perLocation: perLoc,
      });

      // stock
      const stockSnap = stockDocs[i];
      const curQty = (stockSnap.exists() ? stockSnap.data().qty : 0) || 0;
      newStock.set(itemId, curQty + q);

      // pricing
      const poLine = poByItem.get(itemId) || {};
      const itemSnap = itemDocs[i];
      const currentItem = itemSnap.exists() ? itemSnap.data() : {};
      const newCost =
        rec.unitCost != null ? Number(rec.unitCost) :
          (poLine.unitCost != null ? Number(poLine.unitCost) : Number(currentItem.cost || 0));

      let newPrice;
      if (rec.newPrice != null) {
        newPrice = Number(rec.newPrice);
      } else if (rec.margin != null) {
        newPrice = Math.round(newCost * (1 + Number(rec.margin)));
      } else if (currentItem.price != null && Number(currentItem.price) > 0) {
        newPrice = Number(currentItem.price);
      } else {
        newPrice = computeSellingPrice(newCost);
      }
      newItemVals.set(itemId, { cost: newCost, price: newPrice });
    }

    // ======= ÉCRITURES =======
    tx.update(poRef, { items: updatedItems, status: newStatus, updatedAt: serverTimestamp() });

    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const aggRef = aggRefs[i];
      const sRef = stockRefs[i];
      const aggVals = newAgg.get(itemId);
      const newQty = newStock.get(itemId);

      tx.set(
        aggRef,
        {
          onOrder: aggVals.onOrder,
          total: aggVals.total,
          perLocation: aggVals.perLocation,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      tx.set(
        sRef,
        {
          locationId,
          itemId,
          qty: newQty,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const vals = newItemVals.get(itemId);
      const itemRef = itemRefs[i];
      tx.set(
        itemRef,
        {
          cost: vals.cost,
          price: vals.price,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    tx.set(receiptRef, {
      poId,
      locationId,
      items: itemIds.map((id) => ({
        itemId: id,
        qty: recvMap.get(id)?.qty || 0,
        unitCost: recvMap.get(id)?.unitCost ?? null,
        newPrice: recvMap.get(id)?.newPrice ?? null,
        margin: recvMap.get(id)?.margin ?? null,
      })),
      at: serverTimestamp(),
      receivedBy: receivedBy || "anonymous",
    });
  });

  return { receiptId: receiptRef.id };
}

/* =========================================================
 * 2-bis) RECEIVE SHIPMENT
 * - Réception d’un "shipping" (expédition) : shipments/{shipmentId}
 * - Lit shipment + (PO impactés) + stockAgg/stock
 * - Valide bornes: qtyToReceive ≤ (qtyExpédié - rcvdQty) et ≤ restant PO (si poId)
 * - Écrit: stockAgg/stock, PO (qtyReceived + statut), shipment (rcvdQty + statut),
 *          receipt(type=shipment_receive)
 * ========================================================= */
/**
 * @param {{
 *   shipmentId: string,
 *   locationId: string,
 *   lines: Array<{ itemId: string, qty: number, poId?: string }>, // quantités à recevoir
 *   receivedBy?: string
 * }} params
 */
export async function receiveShipment({ shipmentId, locationId, lines, receivedBy }) {
  if (!shipmentId) throw new Error("shipmentId requis");
  if (!Array.isArray(lines) || !lines.length) throw new Error("Aucune ligne à recevoir");

  // On regroupe par (itemId, poId) pour éviter les doublons
  const byKey = new Map(); // `${itemId}__${poId||""}` -> qty
  for (const l of lines) {
    const itemId = l?.itemId;
    const qty = Math.max(0, Number(l?.qty || 0));
    const poId = l?.poId ? String(l.poId) : "";
    if (!itemId || !qty) continue;
    const k = `${itemId}__${poId}`;
    byKey.set(k, (byKey.get(k) || 0) + qty);
  }
  if (!byKey.size) throw new Error("Aucune quantité valide à recevoir");

  // Préparer des structures utiles
  const itemIds = Array.from(new Set(Array.from(byKey.keys()).map((k) => k.split("__")[0])));
  const aggRefs = itemIds.map((id) => doc(db, "stockAgg", id));
  const stockRefs = itemIds.map((id) => doc(db, "stock", stockKey(locationId, id)));

  await runTransaction(db, async (tx) => {
    // === LECTURES ===
    const shipRef = doc(db, "shipments", shipmentId);
    const shipSnap = await tx.get(shipRef);
    if (!shipSnap.exists()) throw new Error("Shipment introuvable");
    const ship = shipSnap.data() || {};
    const shipLines = Array.isArray(ship.lines) ? ship.lines : [];

    // index shipment line par (itemId,poId)
    const slIndex = new Map();
    shipLines.forEach((l, idx) => {
      const key = `${l.itemId}__${l.poId || ""}`;
      slIndex.set(key, { line: l, idx });
    });

    const aggSnaps = await Promise.all(aggRefs.map((r) => tx.get(r)));
    const stockSnaps = await Promise.all(stockRefs.map((r) => tx.get(r)));

    // PO impactés (uniquement lorsqu’un poId est présent)
    const poIds = Array.from(
      new Set(Array.from(byKey.keys()).map((k) => k.split("__")[1]).filter((x) => x))
    );
    const poRefs = poIds.map((id) => doc(db, "purchaseOrders", id));
    const poSnaps = await Promise.all(poRefs.map((r) => tx.get(r)));

    // Construire structure pour PO
    const poById = new Map(); // poId -> { ref, data, items, idx: Map(itemId->poItem), newStatus? }
    for (let i = 0; i < poRefs.length; i++) {
      const r = poRefs[i];
      const s = poSnaps[i];
      if (!s.exists()) throw new Error(`PO introuvable (${r.id}).`);
      const pdata = s.data() || {};
      const items = Array.isArray(pdata.items) ? pdata.items : [];
      const idx = new Map();
      items.forEach((it) => it?.itemId && idx.set(it.itemId, it));
      poById.set(r.id, { ref: r, data: pdata, items, idx });
    }

    // === VALIDATIONS ===
    // 1) bornes Shipment
    for (const [key, qty] of byKey.entries()) {
      const found = slIndex.get(key);
      if (!found) throw new Error(`Ligne d’expédition introuvable (${key})`);
      const { line } = found;
      const shipped = Number(line.qty || 0);
      const already = Number(line.rcvdQty || 0);
      const max = Math.max(0, shipped - already);
      if (qty > max) {
        throw new Error(`Réception > restant expédié pour ${line.name || line.itemId} (reste ${max}).`);
      }
    }

    // 2) bornes PO (si poId)
    for (const [key, qty] of byKey.entries()) {
      const [itemId, poId] = key.split("__");
      if (!poId) continue;
      const p = poById.get(poId);
      if (!p) throw new Error(`PO ${poId} introuvable.`);
      const poLine = p.idx.get(itemId);
      if (!poLine) throw new Error(`Ligne PO manquante pour ${itemId} dans PO ${poId}`);
      const qOrd = Number(poLine.qtyOrdered || 0);
      const qRec = Number(poLine.qtyReceived || 0);
      const remainPO = Math.max(0, qOrd - qRec);
      if (qty > remainPO) {
        throw new Error(`Réception dépasse restant du PO ${poId} (item ${poLine.name || itemId}, reste ${remainPO}).`);
      }
    }

    // === CALCULS ===
    // stockAgg + stock
    const newAgg = new Map();  // itemId -> { onOrder,total,perLocation }
    const newStock = new Map(); // itemId -> newQty (au locationId)

    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const aggSnap = aggSnaps[i];
      const stockSnap = stockSnaps[i];
      const curAgg = aggSnap.exists() ? aggSnap.data() : { onOrder: 0, total: 0, perLocation: {} };
      const perLoc = { ...(curAgg.perLocation || {}) };
      const curLoc = Number(perLoc[locationId] || 0);
      const curTotal = Number(curAgg.total || 0);
      const curOnOrder = Number(curAgg.onOrder || 0);

      // somme reçue pour cet item (toutes lignes confondues)
      const addQty = Array.from(byKey.entries())
        .filter(([k]) => k.split("__")[0] === itemId)
        .reduce((s, [, q]) => s + q, 0);

      // somme qui doit décrémenter onOrder (seulement si poId)
      const addOnOrder = Array.from(byKey.entries())
        .filter(([k]) => {
          const [iid, _poId] = k.split("__");
          return iid === itemId && _poId;
        })
        .reduce((s, [, q]) => s + q, 0);

      perLoc[locationId] = curLoc + addQty;

      newAgg.set(itemId, {
        onOrder: Math.max(0, curOnOrder - addOnOrder),
        total: curTotal + addQty,
        perLocation: perLoc,
      });

      const curQty = (stockSnap.exists() ? stockSnap.data().qty : 0) || 0;
      newStock.set(itemId, curQty + addQty);
    }

    // shipment: rcvdQty += qty; statut
    const newShipLines = [...shipLines];
    for (const [key, qty] of byKey.entries()) {
      const { idx, line } = slIndex.get(key);
      const rcvd = Number(line.rcvdQty || 0) + qty;
      newShipLines[idx] = { ...line, rcvdQty: rcvd };
    }
    let allReceived = true;
    for (const L of newShipLines) {
      const shipped = Number(L.qty || 0);
      const rcvd = Number(L.rcvdQty || 0);
      if (rcvd < shipped) { allReceived = false; break; }
    }
    const newShipStatus = allReceived ? "received" : "partial";

    // PO: qtyReceived + statut (pour poId concernés)
    for (const [poId, info] of poById) {
      // lignes prises pour ce PO
      const adds = Array.from(byKey.entries())
        .filter(([k]) => k.split("__")[1] === poId);

      if (!adds.length) continue;
      // maj qtyReceived
      adds.forEach(([k, qty]) => {
        const itemId = k.split("__")[0];
        const L = info.idx.get(itemId);
        L.qtyReceived = Number(L.qtyReceived || 0) + qty;
      });

      // statut
      let poAll = true;
      let poAny = false;
      for (const it of info.items) {
        const qO = Number(it.qtyOrdered || 0);
        const qR = Number(it.qtyReceived || 0);
        if (qR > 0) poAny = true;
        if (qO > qR) poAll = false;
      }
      info.newStatus = poAll ? "closed" : poAny ? "partial" : "open";
    }

    // === ÉCRITURES ===
    // stockAgg + stock
    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const aggRef = doc(db, "stockAgg", itemId);
      const sRef = doc(db, "stock", stockKey(locationId, itemId));
      const aggVals = newAgg.get(itemId);
      const qtyLoc = newStock.get(itemId);

      tx.set(
        aggRef,
        {
          onOrder: aggVals.onOrder,
          total: aggVals.total,
          perLocation: aggVals.perLocation,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      tx.set(
        sRef,
        {
          locationId,
          itemId,
          qty: qtyLoc,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    // PO impactés
    for (const info of poById.values()) {
      tx.update(info.ref, {
        items: info.items,
        status: info.newStatus,
        updatedAt: serverTimestamp(),
      });
    }

    // shipment
    // const shipRef = doc(db, "shipments", shipmentId);
    tx.update(shipRef, {
      lines: newShipLines,
      status: newShipStatus,
      receivedAt: serverTimestamp(),
      receivedLocation: locationId,
      updatedAt: serverTimestamp(),
    });

    // receipt
    const receiptRef = doc(collection(db, "receipts"));
    tx.set(receiptRef, {
      type: "shipment_receive",
      shipmentId,
      locationId,
      items: Array.from(byKey.entries()).map(([k, qty]) => {
        const [itemId, poId] = k.split("__");
        return { itemId, qty, poId: poId || null };
      }),
      at: serverTimestamp(),
      receivedBy: receivedBy || "anonymous",
    });
  });
}

/* =========================================================
 * 3) RECEIVE MANUAL (hors PO)
 * ========================================================= */
export async function receiveManual({ locationId, items, receivedBy }) {
  // Regroupe par itemId
  const recvByItem = new Map();
  for (const r of items || []) {
    const id = r.itemId;
    const q = Math.max(0, Number(r.qty || 0));
    if (!id || !q) continue;
    const merged = {
      qty: (recvByItem.get(id)?.qty || 0) + q,
      unitCost: typeof r.unitCost === "number" ? r.unitCost : undefined,
      newPrice: typeof r.newPrice === "number" ? r.newPrice : undefined,
      margin: typeof r.margin === "number" ? r.margin : undefined,
    };
    recvByItem.set(id, merged);
  }
  if (!recvByItem.size) throw new Error("Aucune quantité valide à recevoir");

  const itemIds = Array.from(recvByItem.keys());
  const aggRefs = itemIds.map((id) => doc(db, "stockAgg", id));
  const stockRefs = itemIds.map((id) => doc(db, "stock", stockKey(locationId, id)));
  const itemsRefs = itemIds.map((id) => doc(db, "itemsEK", id));
  const receiptRef = doc(collection(db, "receipts"));

  await runTransaction(db, async (tx) => {
    // READS
    const aggDocs = await Promise.all(aggRefs.map((r) => tx.get(r)));
    const stockDocs = await Promise.all(stockRefs.map((r) => tx.get(r)));

    // CALCUL
    const newAgg = new Map();
    const newStock = new Map();

    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const inc = recvByItem.get(itemId).qty;

      const curAgg = aggDocs[i].exists()
        ? aggDocs[i].data()
        : { onOrder: 0, total: 0, perLocation: {} };

      const perLoc = { ...(curAgg.perLocation || {}) };
      const curLoc = Number(perLoc[locationId] || 0);
      perLoc[locationId] = curLoc + inc;

      newAgg.set(itemId, {
        onOrder: Number(curAgg.onOrder || 0), // manuel: ne touche pas onOrder
        total: Number(curAgg.total || 0) + inc,
        perLocation: perLoc,
      });

      const curQty = (stockDocs[i].exists() ? stockDocs[i].data().qty : 0) || 0;
      newStock.set(itemId, curQty + inc);
    }

    // WRITES
    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const aggRef = aggRefs[i];
      const sRef = stockRefs[i];
      const aggVals = newAgg.get(itemId);
      const newQty = newStock.get(itemId);

      tx.set(
        aggRef,
        {
          onOrder: aggVals.onOrder,
          total: aggVals.total,
          perLocation: aggVals.perLocation,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      tx.set(
        sRef,
        {
          locationId,
          itemId,
          qty: newQty,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    // itemsEK (cost/price) si fournis
    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const { unitCost, newPrice } = recvByItem.get(itemId);
      if (typeof unitCost === "number" || typeof newPrice === "number") {
        const patch = { updatedAt: serverTimestamp() };
        if (typeof unitCost === "number") patch.cost = unitCost;
        if (typeof newPrice === "number") patch.price = newPrice;
        tx.set(itemsRefs[i], patch, { merge: true });
      }
    }

    // receipt (poId null)
    tx.set(receiptRef, {
      poId: null,
      locationId,
      items: itemIds.map((id) => ({
        itemId: id,
        qty: recvByItem.get(id).qty,
        unitCost: recvByItem.get(id).unitCost ?? null,
        newPrice: recvByItem.get(id).newPrice ?? null,
      })),
      at: serverTimestamp(),
      receivedBy: receivedBy || "anonymous",
      mode: "manual",
    });
  });

  return { receiptId: receiptRef.id };
}

/* =========================================================
 * 4) LIST INVENTORY SNAPSHOT
 * - Jointure itemsEK + stockAgg (en lecture simple)
 * ========================================================= */
export async function listInventorySnapshot({
  locationId = "boutique",
  search = "",
  limitN = 200,
}) {
  let itemsQ = query(collection(db, "itemsEK"));
  const itemsSnap = await getDocs(itemsQ);

  const results = [];
  const needle = search.trim().toLowerCase();

  for (const d of itemsSnap.docs) {
    const it = { id: d.id, ...d.data() };
    const label = (it.name || it.title || "").toLowerCase();
    const code = (it.barcode || "").toLowerCase();
    if (needle && !label.includes(needle) && !code.includes(needle)) continue;

    const aggSnap = await getDoc(doc(db, "stockAgg", d.id));
    const agg = aggSnap.exists() ? aggSnap.data() : { onOrder: 0, total: 0, perLocation: {} };

    results.push({
      itemId: d.id,
      name: it.name || it.title || "Article",
      barcode: it.barcode || "",
      price: Number(it.price || 0),
      onOrder: Number(agg.onOrder || 0),
      boutique: Number((agg.perLocation || {})[locationId] || 0),
      total: Number(agg.total || 0),
    });

    if (results.length >= limitN) break;
  }
  return results;
}

/* =========================================================
 * 5) REBUILD stockAgg (utile si resync)
 * - total/perLocation depuis stock/*
 * - onOrder depuis purchaseOrders (status open/partial) via PO.items
 * ========================================================= */
export async function rebuildStockAgg() {
  // total + perLocation
  const stockSnap = await getDocs(collection(db, "stock"));
  const agg = {}; // { itemId: { total, perLocation:{} } }
  stockSnap.forEach((s) => {
    const { locationId, itemId, qty } = s.data();
    if (!agg[itemId]) agg[itemId] = { total: 0, perLocation: {} };
    agg[itemId].total += Number(qty || 0);
    agg[itemId].perLocation[locationId] =
      Number(agg[itemId].perLocation[locationId] || 0) + Number(qty || 0);
  });

  // onOrder depuis les PO ouverts/partiels
  const poSnap = await getDocs(
    query(collection(db, "purchaseOrders"), where("status", "in", ["open", "partial"]))
  );
  const onOrder = {}; // { itemId: number }
  poSnap.forEach((po) => {
    const data = po.data() || {};
    const items = Array.isArray(data.items) ? data.items : [];
    for (const L of items) {
      const itemId = L?.itemId;
      if (!itemId) continue;
      const remain = Math.max(0, Number(L.qtyOrdered || 0) - Number(L.qtyReceived || 0)) || 0;
      onOrder[itemId] = Number(onOrder[itemId] || 0) + remain;
    }
  });

  // push vers stockAgg/*
  const batch = writeBatch(db);
  const ids = new Set([...Object.keys(agg), ...Object.keys(onOrder)]);
  ids.forEach((itemId) => {
    const total = agg[itemId]?.total || 0;
    const perLocation = agg[itemId]?.perLocation || {};
    const oo = onOrder[itemId] || 0;
    batch.set(
      doc(db, "stockAgg", itemId),
      { total, perLocation, onOrder: oo, updatedAt: serverTimestamp() },
      { merge: true }
    );
  });
  await batch.commit();
}

// --- SHIPMENTS: création & alimentation ---

/**
 * Crée un shipping vide (draft).
 * @param {{origin?:string,destination?:string,createdBy?:string,meta?:any}} params
 * @returns {Promise<{id:string}>}
 */
export async function createShipment({ origin = "supplier", destination = "boutique", createdBy = "anonymous", meta = {} } = {}) {
  const ref = doc(collection(db, "shipments"));
  await runTransaction(db, async (tx) => {
    tx.set(ref, {
      status: "draft",                // draft -> shipped -> partial/received
      origin,
      destination,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      meta,
      lines: [],                      // {itemId, name?, poId?, qty, rcvdQty}
    });
  });
  return { id: ref.id };
}

/**
 * Ajoute (ou remplace) des lignes dans un shipping (et reste en draft/shipped selon param).
 * @param {{shipmentId:string, lines:Array<{itemId:string,poId?:string,qty:number,name?:string}>, markShipped?:boolean}} params
 */
export async function addShipmentLines({ shipmentId, lines, markShipped = false }) {
  if (!shipmentId) throw new Error("shipmentId requis");
  if (!Array.isArray(lines) || !lines.length) throw new Error("Aucune ligne fournie");

  const shipRef = doc(db, "shipments", shipmentId);

  await runTransaction(db, async (tx) => {
    const sSnap = await tx.get(shipRef);
    if (!sSnap.exists()) throw new Error("Shipment introuvable");
    const ship = sSnap.data() || {};
    if (ship.status !== "draft" && ship.status !== "shipped") {
      throw new Error("Impossible de modifier ce shipping sur ce statut");
    }

    // index existant par (itemId, poId)
    const cur = Array.isArray(ship.lines) ? [...ship.lines] : [];
    const idx = new Map();
    cur.forEach((l, i) => idx.set(`${l.itemId}__${l.poId || ""}`, i));

    // merge
    for (const L of lines) {
      const key = `${L.itemId}__${L.poId || ""}`;
      const entry = {
        itemId: L.itemId,
        poId: L.poId || null,
        name: L.name || null,
        qty: Math.max(0, Number(L.qty || 0)),
        rcvdQty: 0, // réinitialisé si on remplace
      };
      if (idx.has(key)) {
        cur[idx.get(key)] = { ...cur[idx.get(key)], ...entry, rcvdQty: cur[idx.get(key)].rcvdQty || 0 };
      } else {
        cur.push(entry);
      }
    }

    tx.update(shipRef, {
      lines: cur,
      status: markShipped ? "shipped" : ship.status,
      updatedAt: serverTimestamp(),
      shippedAt: markShipped ? serverTimestamp() : ship.shippedAt || null,
    });
  });
}

/**
 * Remplit un shipping depuis le restant d’un PO (toutes lignes ou sélection).
 * @param {{shipmentId:string, poId:string, itemFilter?:string[]}} params
 */
export async function shipPOItems({ shipmentId, poId, itemFilter = null }) {
  if (!shipmentId) throw new Error("shipmentId requis");
  if (!poId) throw new Error("poId requis");

  const shipRef = doc(db, "shipments", shipmentId);
  const poRef = doc(db, "purchaseOrders", poId);

  await runTransaction(db, async (tx) => {
    const [sSnap, poSnap] = await Promise.all([tx.get(shipRef), tx.get(poRef)]);
    if (!sSnap.exists()) throw new Error("Shipment introuvable");
    if (!poSnap.exists()) throw new Error("PO introuvable");

    const ship = sSnap.data() || {};
    if (ship.status !== "draft" && ship.status !== "shipped") {
      throw new Error("Le shipping ne peut pas être alimenté sur ce statut");
    }
    const po = poSnap.data() || {};
    const poItems = Array.isArray(po.items) ? po.items : [];

    const targetIds = itemFilter && itemFilter.length ? new Set(itemFilter) : null;

    // index shipping existant
    const cur = Array.isArray(ship.lines) ? [...ship.lines] : [];
    const idx = new Map();
    cur.forEach((l, i) => idx.set(`${l.itemId}__${l.poId || ""}`, i));

    // pour chaque ligne PO, ajoute le restant
    for (const L of poItems) {
      if (!L?.itemId) continue;
      if (targetIds && !targetIds.has(L.itemId)) continue;
      const remain = Math.max(0, Number(L.qtyOrdered || 0) - Number(L.qtyReceived || 0));
      if (remain <= 0) continue;

      const key = `${L.itemId}__${poId}`;
      const entry = {
        itemId: L.itemId,
        poId,
        name: L.name || null,
        qty: remain,
        rcvdQty: 0,
      };
      if (idx.has(key)) {
        const at = idx.get(key);
        const prev = cur[at];
        cur[at] = { ...prev, qty: remain, name: prev.name || entry.name || null };
      } else {
        cur.push(entry);
      }
    }

    tx.update(shipRef, {
      lines: cur,
      updatedAt: serverTimestamp(),
    });

    // 🔒 on verrouille le PO ici
    tx.update(poRef, {
      locked: true,
      updatedAt: serverTimestamp(),
    });
  });

  
}

/**
 * Fige un shipping en "shipped" (plus de modifs de quantité sans réception).
 * @param {{shipmentId:string}} params
 */
export async function finalizeShipment({ shipmentId }) {
  if (!shipmentId) throw new Error("shipmentId requis");
  const shipRef = doc(db, "shipments", shipmentId);
  await runTransaction(db, async (tx) => {
    const sSnap = await tx.get(shipRef);
    if (!sSnap.exists()) throw new Error("Shipment introuvable");
    const ship = sSnap.data() || {};
    if (ship.status !== "draft") return; // déjà shipped/partial/received
    tx.update(shipRef, {
      status: "shipped",
      shippedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
}

/* =========================================================
 * 6) ITEM HELPERS (édition et synchronisation)
 * ========================================================= */
export async function upsertItemEK(itemId, { name, barcode, ...rest }) {
  const ref = doc(db, "itemsEK", itemId);
  const patch = {
    ...rest,
    name: name || rest.name || null,
    barcode: barcode || rest.barcode || "",
    nameLower: (name || rest.name || "").toLowerCase(),
    updatedAt: serverTimestamp(),
  };
  await runTransaction(db, async (tx) => {
    tx.set(ref, patch, { merge: true });
    if (patch.barcode) {
      tx.set(doc(db, "barcodes", patch.barcode), { itemId }, { merge: true });
    }
  });
}

/* =========================================================
 * Exports utilitaires
 * ========================================================= */
export { stockKey, computeSellingPrice };
